// ============================================================================
// Seats — "2 of 3" on a hiring card, counted live.
//
// A hiring request is ONE SEAT (Ben, 2026-09-22, Build Room item 621). A
// request written for N people is stored by the database as N one-seat
// requests, the extra N-1 pointing at the first through
// `split_from_request_id`. The group's size is never stored: it is counted
// here, from the rows themselves, every time the board loads.
//
// Numbering is by request id, which the id trigger mints in order, so the
// first seat is always "1 of N" and an opening added later is always last.
// Every member counts, whatever its stage — a Disregarded seat is still one of
// the openings that was asked for, and dropping it from the count would
// renumber the cards people are already talking about.
// ============================================================================

export interface SeatFields {
  request_id: string;
  split_from_request_id: string | null;
  headcount: number | null;
}

export interface Seat {
  /** 1-based position in the group. Null when the request stands alone. */
  seat_number: number | null;
  /** How many requests are in the group. Null when the request stands alone. */
  seat_count: number | null;
}

/** The group a request belongs to: its first seat's id. */
export function groupOf(row: SeatFields): string {
  return row.split_from_request_id ?? row.request_id;
}

/**
 * Seat number and group size for every row, keyed by request id. Pass the
 * WHOLE list, not a filtered one — a filter that hides a sibling must not
 * change the count on the card that is still showing.
 */
export function seatsFor(rows: readonly SeatFields[]): Map<string, Seat> {
  const groups = new Map<string, string[]>();
  for (const r of rows) {
    const g = groupOf(r);
    const members = groups.get(g);
    if (members) members.push(r.request_id);
    else groups.set(g, [r.request_id]);
  }
  const out = new Map<string, Seat>();
  for (const members of groups.values()) {
    members.sort();
    const n = members.length;
    members.forEach((id, i) => {
      out.set(id, n > 1 ? { seat_number: i + 1, seat_count: n } : { seat_number: null, seat_count: null });
    });
  }
  return out;
}

/**
 * The label a card or a list row shows. A seat in a group reads "2 of 3". A
 * request on its own keeps the label it always had ("1 hire"), and the handful
 * of historical multi-seat requests, which predate the split, keep theirs.
 */
export function seatLabel(row: SeatFields & Seat): string {
  if (row.seat_number != null && row.seat_count != null) {
    return `${row.seat_number} of ${row.seat_count}`;
  }
  const n = row.headcount ?? 1;
  return `${n} ${n === 1 ? 'hire' : 'hires'}`;
}
